import { Component, lazy, Suspense, useCallback, useState } from "react";
import type { ComponentType, ErrorInfo, LazyExoticComponent, ReactNode } from "react";

import type { ContainerLoader } from "containers/Application/lazyContainers";

interface LazyBoundaryProps {
  name: string;
  // Usually the container's gate. A change while in the error state clears the error and calls
  // `onReset`, so closing and reopening a modal whose chunk failed retries the import.
  resetKey: unknown;
  onReset?: () => void;
  children?: ReactNode;
}

interface LazyBoundaryState {
  error: unknown;
}

// Contains a failed chunk load (or a render error) to the one container it wraps, so a 404'd
// chunk after a deploy renders nothing for that feature instead of unmounting the whole lobby
// (PLAY-5251 design D6). No auto-reload: that can loop.
export class LazyBoundary extends Component<LazyBoundaryProps, LazyBoundaryState> {
  state: LazyBoundaryState = { error: null };

  static getDerivedStateFromError(error: unknown): LazyBoundaryState {
    return { error };
  }

  componentDidCatch(error: unknown, _info: ErrorInfo) {
    console.error(`[LazyBoundary] ${this.props.name} failed to load or render`, error);
  }

  componentDidUpdate(prevProps: LazyBoundaryProps) {
    if (this.state.error && prevProps.resetKey !== this.props.resetKey) {
      this.props.onReset?.();
      this.setState({ error: null });
    }
  }

  render() {
    return this.state.error ? null : this.props.children;
  }
}

interface LazyContainerProps {
  name: string;
  load: ContainerLoader;
  fallback: ReactNode;
  resetKey?: unknown;
}

// One `lazy()` per loader, shared by every mount, so a remount (e.g. a route switch) reuses the
// resolved component instead of suspending again and flashing the skeleton.
const lazyCache = new Map<ContainerLoader, LazyExoticComponent<ComponentType<any>>>();

function lazyFor(load: ContainerLoader, fresh = false) {
  let component = lazyCache.get(load);
  if (!component || fresh) {
    component = lazy(load);
    lazyCache.set(load, component);
  }
  return component;
}

// One lazy container: a `Suspense` with the given fallback inside a `LazyBoundary`. On reset it
// builds a fresh `lazy()` from the loader, because React caches a rejected `lazy()` promise and
// would otherwise never request the chunk again.
export function LazyContainer({ name, load, fallback, resetKey }: LazyContainerProps) {
  const [LoadedContainer, setLoadedContainer] = useState(() => lazyFor(load));
  const onReset = useCallback(() => setLoadedContainer(() => lazyFor(load, true)), [load]);

  return (
    <LazyBoundary name={name} resetKey={resetKey} onReset={onReset}>
      <Suspense fallback={fallback}>
        <LoadedContainer />
      </Suspense>
    </LazyBoundary>
  );
}

export default LazyContainer;
